'use client'

import { useEffect, useState } from 'react'
import { useTranslations } from 'next-intl'

import CloseIcon from '@/assets/icons/close.svg'
import DownloadIcon from '@/assets/icons/download.svg'
import { FileUploadItem, ImagePreviewItem } from '@/components/ui/file-item'
import { Skeleton } from '@/components/ui/skeleton'
import { useFileAction } from '@/hooks/useFileAction'
import { getFileType, isImageFile } from '@/lib/file'
import type { ChatAttachment as ChatAttachmentData } from '@/types/chat'

interface ChatAttachmentsProps {
  attachments: ChatAttachmentData[]
}

function ChatAttachment({ file }: { file: ChatAttachmentData['file'] }) {
  const t = useTranslations('common')
  const [preview, setPreview] = useState<{ source: number; url: string | null } | null>(null)
  const { isPending, progress, download, cancelDownload, getFile } = useFileAction({
    fileId: file.id,
    fileName: file.name,
    file: file.localFile,
  })
  const isImage = isImageFile(file.name, file.localFile)

  useEffect(() => {
    if (!isImage) return

    let cancelled = false
    let objectUrl: string | undefined

    void (async () => {
      try {
        const { blob } = await getFile()
        if (cancelled) return
        objectUrl = URL.createObjectURL(blob)
        const image = new Image()
        image.src = objectUrl
        await image.decode()
        if (!cancelled) setPreview({ source: file.id, url: objectUrl })
      } catch {
        if (objectUrl) URL.revokeObjectURL(objectUrl)
        objectUrl = undefined
        if (!cancelled) setPreview({ source: file.id, url: null })
      }
    })()

    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [isImage, file.id, getFile])

  const currentPreview = preview?.source === file.id ? preview : null

  if (isImage && !currentPreview) return <Skeleton className="size-20 shrink-0 rounded-200" />
  if (isImage && currentPreview?.url) return <ImagePreviewItem fileUrl={currentPreview.url} />

  return (
    <FileUploadItem
      fileName={file.name}
      fileDescription={getFileType(file.name)}
      progress={progress}
      actions={[
        {
          icon: isPending ? (
            <CloseIcon className="size-6" aria-label={`${t('cancel')} ${file.name}`} />
          ) : (
            <DownloadIcon className="size-6" aria-label={`${t('download')} ${file.name}`} />
          ),
          action: isPending ? cancelDownload : download,
        },
      ]}
    />
  )
}

export function ChatAttachments({ attachments }: ChatAttachmentsProps) {
  const images = attachments.filter((attachment) => isImageFile(attachment.file.name, attachment.file.localFile))
  const files = attachments.filter((attachment) => !isImageFile(attachment.file.name, attachment.file.localFile))

  return (
    <>
      {images.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {images.map(({ id, file }) => (
            <ChatAttachment key={`${id}-${file.id}-${file.name}`} file={file} />
          ))}
        </div>
      )}
      {files.length > 0 && (
        <div className="flex flex-col gap-2">
          {files.map(({ id, file }) => (
            <ChatAttachment key={`${id}-${file.id}-${file.name}`} file={file} />
          ))}
        </div>
      )}
    </>
  )
}
